import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, PhoneIncoming, X, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
// Note: Socket is now passed from Chat.js to prevent multiple connections
import axios from 'axios';
import { useClickLock } from '../../hooks';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to log calls in the chat
const logCallToChat = async (conversationId, callType, callStatus, callDuration, callerId) => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('call_type', callType);
    formData.append('call_status', callStatus);
    formData.append('call_duration', callDuration.toString());
    formData.append('caller_id', callerId);
    
    await axios.post(
      `${API}/api/messages/conversation/${conversationId}/call-log`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    console.log(`[CallInterface] Call logged: ${callStatus} ${callType} call`);
  } catch (error) {
    console.error('[CallInterface] Failed to log call:', error);
  }
};

// ICE servers will be fetched from backend API (Twilio)
// Use 'all' transport policy to try direct connection first, fallback to TURN
let ICE_SERVERS = {
  iceServers: [],  // Will be populated from backend
  iceCandidatePoolSize: 10,
  iceTransportPolicy: 'all'  // Try direct first, TURN only when needed
};

// Optimized media constraints for better performance
const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sampleRate: 48000,
  channelCount: 1  // Mono for voice calls (lighter)
};

const VIDEO_CONSTRAINTS_LOW = {
  width: { ideal: 480, max: 640 },
  height: { ideal: 360, max: 480 },
  frameRate: { ideal: 15, max: 24 }  // Lower framerate for mobile
};

const VIDEO_CONSTRAINTS_HIGH = {
  width: { ideal: 640, max: 1280 },
  height: { ideal: 480, max: 720 },
  frameRate: { ideal: 24, max: 30 }
};

// Fetch ICE servers from backend (Twilio TURN)
const fetchIceServers = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/api/calls/ice-servers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    console.log('[CallInterface] ✅ Fetched Twilio ICE servers:', response.data.iceServers?.length, 'servers');
    console.log('[CallInterface] ICE servers:', response.data.iceServers?.map(s => s.urls || s.url));
    ICE_SERVERS = response.data;
    return response.data;
  } catch (error) {
    console.error('[CallInterface] ❌ CRITICAL: Failed to fetch ICE servers:', error);
    toast.error('Failed to initialize call servers. Please try again.');
    throw error;  // Don't fallback to free servers - fail fast
  }
};

export default function CallInterface({ 
  conversation, 
  currentUser, 
  otherUser,
  incomingCall,
  socket: chatSocket,  // Socket from Chat.js
  pendingOffer: initialPendingOffer,  // Offer captured by Chat.js before CallInterface mounted
  initiateCallType,  // If set, auto-start call of this type (no confirmation needed)
  onClose 
}) {
  const [callState, setCallState] = useState(incomingCall ? 'ringing' : 'idle');
  const [callType, setCallType] = useState(incomingCall?.call_type || 'audio');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callId, setCallId] = useState(incomingCall?.call_id || null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  // Speaker mode: video calls default to speaker ON, audio calls default to earpiece (speaker OFF)
  const [isSpeakerOn, setIsSpeakerOn] = useState(incomingCall?.call_type === 'video' || false);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [isReconnecting, setIsReconnecting] = useState(false);  // Reconnection state
  
  // PiP (Picture-in-Picture) position state for draggable local video
  const [pipPosition, setPipPosition] = useState({ x: null, y: null }); // null means use default position
  const [isDragging, setIsDragging] = useState(false);
  const pipRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, pipX: 0, pipY: 0 });

  // Click lock for reject button
  const { loading: rejectLoading, execute: executeReject } = useClickLock();

  // Handle incoming call prop changes (when component is already mounted)
  useEffect(() => {
    if (incomingCall) {
      console.log('[CallInterface] Setting up for incoming call:', incomingCall);
      setCallState('ringing');
      setCallType(incomingCall.call_type);
      setCallId(incomingCall.call_id);
      setIsSpeakerOn(incomingCall.call_type === 'video');
    }
  }, [incomingCall]);
  const [selectedOutputDevice, setSelectedOutputDevice] = useState('default');
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const combinedStreamRef = useRef(null);  // For recording both local and remote
  const pendingCandidatesRef = useRef([]);
  const pendingOfferRef = useRef(null);  // Store offer until user accepts
  const callIdRef = useRef(callId);
  const hasAcceptedRef = useRef(false);  // Track if user has accepted
  const hasLoggedCallRef = useRef(false);  // Prevent duplicate call logging
  const wakeLockRef = useRef(null);  // Wake Lock to prevent screen sleep
  
  // Keep callId ref updated
  useEffect(() => {
    callIdRef.current = callId;
  }, [callId]);
  
  const silentAudioRef = useRef(null);  // Silent audio for iOS background keepalive
  
  // Request Wake Lock to prevent screen from sleeping during call
  const requestWakeLock = async () => {
    try {
      // Standard Wake Lock API for Android/Desktop
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('[CallInterface] ✅ Wake Lock acquired - screen will stay on');
        
        wakeLockRef.current.addEventListener('release', () => {
          console.log('[CallInterface] ⚠️ Wake Lock released');
          // Try to re-acquire if still in a call
          if (callState === 'active') {
            requestWakeLock();
          }
        });
      } else {
        console.log('[CallInterface] Wake Lock API not supported');
      }
      
      // iOS-specific: Play silent audio to keep app alive when screen locks
      // This prevents iOS from suspending the app/WebRTC connection
      startSilentAudio();
      
    } catch (err) {
      console.error('[CallInterface] Wake Lock error:', err);
    }
  };
  
  // Play silent audio to keep iOS app alive during calls
  const startSilentAudio = () => {
    try {
      // Create a silent audio context
      if (!silentAudioRef.current) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const audioContext = new AudioContextClass();
          
          // Create a gain node with volume 0 (silent)
          const gainNode = audioContext.createGain();
          gainNode.gain.value = 0;
          
          // Create an oscillator (generates tone but at 0 volume = silent)
          const oscillator = audioContext.createOscillator();
          oscillator.frequency.value = 200; // Low frequency
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.start();
          
          silentAudioRef.current = { audioContext, oscillator };
          console.log('[CallInterface] 🔇 Silent audio started for iOS keepalive');
        }
      }
    } catch (e) {
      console.warn('[CallInterface] Could not start silent audio:', e);
    }
  };
  
  // Stop silent audio
  const stopSilentAudio = () => {
    if (silentAudioRef.current) {
      try {
        silentAudioRef.current.oscillator.stop();
        silentAudioRef.current.audioContext.close();
        silentAudioRef.current = null;
        console.log('[CallInterface] 🔇 Silent audio stopped');
      } catch (e) {
        // Ignore errors during cleanup
      }
    }
  };
  
  // Release Wake Lock
  const releaseWakeLock = async () => {
    // Stop silent audio
    stopSilentAudio();
    
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('[CallInterface] Wake Lock released');
      } catch (err) {
        console.error('[CallInterface] Error releasing Wake Lock:', err);
      }
    }
  };

  // Safe call logging wrapper - prevents duplicate logs
  const safeLogCallToChat = async (convId, cType, status, duration, callerId) => {
    // Prevent duplicate logging
    if (hasLoggedCallRef.current) {
      console.log('[CallInterface] Call already logged, skipping duplicate');
      return;
    }
    hasLoggedCallRef.current = true;
    
    await logCallToChat(convId, cType, status, duration, callerId);
  };

  // Process pending offer from Chat.js (captured before this component mounted)
  useEffect(() => {
    if (initialPendingOffer && !pendingOfferRef.current) {
      console.log('[CallInterface] Processing offer captured by Chat.js');
      pendingOfferRef.current = initialPendingOffer;
      
      // If user has already accepted, process the offer immediately
      if (hasAcceptedRef.current) {
        console.log('[CallInterface] User already accepted, processing captured offer...');
        handleIncomingOffer(initialPendingOffer);
      }
    }
  }, [initialPendingOffer]);

  // Fetch ICE servers on component mount
  useEffect(() => {
    fetchIceServers().then(() => {
      console.log('[CallInterface] ICE servers ready');
    });
  }, []);

  // Enumerate audio output devices (for speaker/bluetooth selection)
  useEffect(() => {
    const enumerateDevices = async () => {
      try {
        // Need to get permission first to see device labels
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outputs = devices.filter(d => d.kind === 'audiooutput');
        console.log('[CallInterface] Available audio outputs:', outputs.map(d => ({
          id: d.deviceId,
          label: d.label || 'Unknown device'
        })));
        setAudioOutputDevices(outputs);
      } catch (e) {
        console.log('[CallInterface] Could not enumerate audio devices:', e);
      }
    };
    enumerateDevices();
    
    // Listen for device changes (e.g., Bluetooth connected/disconnected)
    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
    };
  }, []);

  // Auto-initiate call if initiateCallType is provided (no confirmation needed)
  // But DON'T auto-initiate if this is an incoming call (ringing state)
  useEffect(() => {
    if (initiateCallType && isSocketConnected && callState === 'idle' && !incomingCall) {
      console.log('[CallInterface] Auto-initiating', initiateCallType, 'call');
      initiateCall(initiateCallType);
    }
  }, [initiateCallType, isSocketConnected, callState, incomingCall]);

  // Function to switch audio output device
  const switchAudioOutput = async (deviceId) => {
    try {
      if (remoteVideoRef.current && typeof remoteVideoRef.current.setSinkId === 'function') {
        await remoteVideoRef.current.setSinkId(deviceId);
        setSelectedOutputDevice(deviceId);
        console.log('[CallInterface] Switched audio output to:', deviceId);
        
        // Update speaker state based on device
        const device = audioOutputDevices.find(d => d.deviceId === deviceId);
        const isSpeaker = device?.label?.toLowerCase().includes('speaker') || deviceId === 'default';
        setIsSpeakerOn(isSpeaker);
        
        toast.success(`Audio output: ${device?.label || 'Default'}`);
      } else {
        console.warn('[CallInterface] setSinkId not supported in this browser');
        toast.error('Audio output switching not supported on this device');
      }
    } catch (error) {
      console.error('[CallInterface] Error switching audio output:', error);
      toast.error('Failed to switch audio output');
    }
  };

  // Toggle speaker mode
  const toggleSpeaker = async () => {
    // Check if setSinkId is supported
    const audioElement = remoteVideoRef.current;
    
    if (!audioElement || typeof audioElement.setSinkId !== 'function') {
      // On iOS and some mobile browsers, setSinkId is not supported
      // Just toggle the visual state and show info message
      setIsSpeakerOn(!isSpeakerOn);
      toast.info(
        !isSpeakerOn ? '🔊 Speaker mode (hardware control may be limited on mobile)' : '📱 Earpiece mode',
        { duration: 2000 }
      );
      return;
    }
    
    try {
      // Find speakerphone device or default
      const speakerDevice = audioOutputDevices.find(d => 
        d.label.toLowerCase().includes('speaker') && !d.label.toLowerCase().includes('ear')
      );
      
      // Find earpiece device
      const earpieceDevice = audioOutputDevices.find(d => 
        d.label.toLowerCase().includes('earpiece') || d.label.toLowerCase().includes('ear')
      ) || audioOutputDevices.find(d => !d.label.toLowerCase().includes('speaker'));
      
      if (isSpeakerOn && earpieceDevice) {
        await audioElement.setSinkId(earpieceDevice.deviceId);
        setSelectedOutputDevice(earpieceDevice.deviceId);
        setIsSpeakerOn(false);
        toast.success('📱 Earpiece', { duration: 1500 });
      } else if (!isSpeakerOn && speakerDevice) {
        await audioElement.setSinkId(speakerDevice.deviceId);
        setSelectedOutputDevice(speakerDevice.deviceId);
        setIsSpeakerOn(true);
        toast.success('🔊 Speaker', { duration: 1500 });
      } else {
        // No specific devices found, just toggle state
        setIsSpeakerOn(!isSpeakerOn);
        toast.info(!isSpeakerOn ? '🔊 Speaker' : '📱 Earpiece', { duration: 1500 });
      }
    } catch (error) {
      console.error('[CallInterface] Error toggling speaker:', error);
      // Still toggle the state visually even if setSinkId fails
      setIsSpeakerOn(!isSpeakerOn);
      toast.info(!isSpeakerOn ? '🔊 Speaker' : '📱 Earpiece', { duration: 1500 });
    }
  };

  // Initialize socket connection - USE THE SOCKET FROM CHAT.JS
  // This prevents creating multiple socket connections that overwrite each other
  useEffect(() => {
    console.log('[CallInterface] Initializing with shared socket...');
    console.log('[CallInterface] Current user:', currentUser?.id, currentUser?.username);
    console.log('[CallInterface] Other user:', otherUser?.id, otherUser?.name);
    console.log('[CallInterface] Chat socket connected:', chatSocket?.connected);
    
    if (!chatSocket) {
      console.error('[CallInterface] No socket provided from Chat.js!');
      setStatusMessage('Connection error - please refresh the page');
      return;
    }
    
    // Use the socket from Chat.js - DO NOT create a new one!
    socketRef.current = chatSocket;
    
    if (chatSocket.connected) {
      setIsSocketConnected(true);
      setStatusMessage('Connected to server');
      console.log('[CallInterface] Using existing socket connection, SID:', chatSocket.id);
    }
    
    // Set up call-specific event handlers
    const onConnect = () => {
      console.log('[CallInterface] Socket connected! SID:', chatSocket.id);
      setIsSocketConnected(true);
      setStatusMessage('Connected to server');
    };
    
    const onDisconnect = (reason) => {
      console.log('[CallInterface] Socket disconnected:', reason);
      setIsSocketConnected(false);
      setStatusMessage('Disconnected from server');
    };
    
    const onConnectError = (error) => {
      console.error('[CallInterface] Socket connection error:', error);
      setStatusMessage('Connection error: ' + error.message);
    };
    
    const onIncomingCall = (data) => {
      console.log('[CallInterface] Incoming call event (should be handled by Chat.js):', data);
    };
    
    const onCallAccepted = (data) => {
      console.log('[CallInterface] Call accepted:', data);
      setStatusMessage('Call accepted, connecting...');
    };
    
    const onCallRejected = (data) => {
      console.log('[CallInterface] Call rejected:', data);
      toast.error('Call was declined');
      
      // Don't log here - the receiver already logged when they rejected
      // This prevents duplicate call logs
      
      cleanup();
      setCallState('ended');
      setTimeout(() => onClose(), 2000);
    };
    
    const onCallEnded = (data) => {
      console.log('[CallInterface] Call ended by peer:', data);
      toast.info('Call ended');
      
      // Don't log here - the person who ended the call already logged it
      // This prevents duplicate call logs
      
      cleanup();
      setCallState('ended');
      setTimeout(() => onClose(), 2000);
    };
    
    const onCallUnavailable = (data) => {
      console.log('[CallInterface] Call unavailable:', data);
      toast.error(data.reason || 'User is unavailable');
      
      // Log missed/no_answer call - only caller logs this since receiver never saw the call
      if (conversation?.id) {
        safeLogCallToChat(
          conversation.id,
          callType,
          'no_answer',
          0,
          currentUser?.id  // We were the caller
        );
      }
      
      cleanup();
      setCallState('ended');
      setTimeout(() => onClose(), 2000);
    };
    
    const onWebrtcOffer = async (data) => {
      console.log('[CallInterface] Received WebRTC offer, hasAccepted:', hasAcceptedRef.current);
      
      // Store the offer - we'll process it when user accepts the call
      pendingOfferRef.current = data;
      
      // If user has already clicked accept, process the offer now
      if (hasAcceptedRef.current) {
        console.log('[CallInterface] User already accepted, processing offer now...');
        await handleIncomingOffer(data);
      } else {
        console.log('[CallInterface] Storing offer, waiting for user to accept...');
      }
    };
    
    const onWebrtcAnswer = async (data) => {
      console.log('[CallInterface] Received WebRTC answer');
      await handleIncomingAnswer(data);
    };
    
    const onWebrtcIceCandidate = async (data) => {
      console.log('[CallInterface] Received ICE candidate');
      await handleIncomingIceCandidate(data);
    };
    
    const onCallConnectionLost = (data) => {
      console.log('[CallInterface] ⚠️ Received call_connection_lost event:', data);
      // Don't immediately end the call - WebRTC has its own reconnection logic
      // Only show a warning message
      setStatusMessage('Connection interrupted, trying to reconnect...');
      
      // WebRTC will try to reconnect via ICE restart
      // Only end the call if we receive this after multiple failures
      // or if the call was never properly connected
      if (callState === 'connecting' || callState === 'ringing') {
        // Call never connected properly - OK to end
        toast.error('Could not connect to other user');
        cleanup();
        setCallState('ended');
        setTimeout(() => onClose(), 2000);
      }
      // If call was connected, don't end it - let WebRTC try to recover
    };
    
    const onCallConnectionFailed = (data) => {
      console.log('[CallInterface] ⚠️ Peer connection failed:', data);
      toast.error('Connection failed on other end');
      cleanup();
      setCallState('ended');
      setStatusMessage('Connection failed');
      setTimeout(() => onClose(), 2000);
    };
    
    // Add event listeners
    chatSocket.on('connect', onConnect);
    chatSocket.on('disconnect', onDisconnect);
    chatSocket.on('connect_error', onConnectError);
    chatSocket.on('incoming_call', onIncomingCall);
    chatSocket.on('call_accepted', onCallAccepted);
    chatSocket.on('call_rejected', onCallRejected);
    chatSocket.on('call_ended', onCallEnded);
    chatSocket.on('call_unavailable', onCallUnavailable);
    chatSocket.on('call_connection_lost', onCallConnectionLost);
    chatSocket.on('call_connection_failed', onCallConnectionFailed);
    chatSocket.on('webrtc_offer', onWebrtcOffer);
    chatSocket.on('webrtc_answer', onWebrtcAnswer);
    chatSocket.on('webrtc_ice_candidate', onWebrtcIceCandidate);
    
    return () => {
      console.log('[CallInterface] Removing call event handlers...');
      // Remove only our event handlers, don't disconnect the shared socket!
      chatSocket.off('connect', onConnect);
      chatSocket.off('disconnect', onDisconnect);
      chatSocket.off('connect_error', onConnectError);
      chatSocket.off('incoming_call', onIncomingCall);
      chatSocket.off('call_accepted', onCallAccepted);
      chatSocket.off('call_rejected', onCallRejected);
      chatSocket.off('call_ended', onCallEnded);
      chatSocket.off('call_unavailable', onCallUnavailable);
      chatSocket.off('call_connection_lost', onCallConnectionLost);
      chatSocket.off('call_connection_failed', onCallConnectionFailed);
      chatSocket.off('webrtc_offer', onWebrtcOffer);
      chatSocket.off('webrtc_answer', onWebrtcAnswer);
      chatSocket.off('webrtc_ice_candidate', onWebrtcIceCandidate);
      cleanup();
    };
  }, [chatSocket]);
  
  // Timer for call duration
  useEffect(() => {
    let interval;
    if (callState === 'active' && callStartTime) {
      interval = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [callState, callStartTime]);
  
  const cleanup = () => {
    console.log('[CallInterface] Cleanup called');
    
    // Stop recording first
    stopRecording();
    
    // Release Wake Lock
    releaseWakeLock();
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('[CallInterface] Stopped track:', track.kind);
      });
      localStreamRef.current = null;
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
      console.log('[CallInterface] Closed peer connection');
    }
  };
  
  const getMediaStream = async (includeVideo) => {
    console.log('[CallInterface] Getting media stream, video:', includeVideo);
    
    try {
      // Use optimized constraints - audio first (lighter), then video
      const constraints = {
        audio: AUDIO_CONSTRAINTS,
        video: includeVideo ? {
          ...VIDEO_CONSTRAINTS_LOW,  // Start with low quality for faster connection
          facingMode: 'user'
        } : false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[CallInterface] Got media stream:', stream.getTracks().map(t => t.kind));
      
      // Apply bitrate limits to video track
      if (includeVideo && stream.getVideoTracks().length > 0) {
        const videoTrack = stream.getVideoTracks()[0];
        const settings = videoTrack.getSettings();
        console.log('[CallInterface] Video settings:', settings.width, 'x', settings.height, '@', settings.frameRate, 'fps');
      }
      
      return stream;
    } catch (error) {
      console.error('[CallInterface] Media error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Please allow camera/microphone access');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera/microphone found');
      } else {
        toast.error('Could not access media devices');
      }
      throw error;
    }
  };
  
  const createPeerConnection = () => {
    console.log('[CallInterface] ====================================');
    console.log('[CallInterface] Creating RTCPeerConnection...');
    console.log('[CallInterface] ICE Transport Policy:', ICE_SERVERS.iceTransportPolicy);
    console.log('[CallInterface] ICE Servers:');
    ICE_SERVERS.iceServers.forEach((server, i) => {
      const url = server.urls || server.url;
      const hasAuth = !!server.credential;
      console.log(`[CallInterface]   ${i + 1}. ${url} (auth: ${hasAuth})`);
    });
    console.log('[CallInterface] ====================================');
    
    if (!ICE_SERVERS.iceServers || ICE_SERVERS.iceServers.length === 0) {
      console.error('[CallInterface] ❌ NO ICE SERVERS CONFIGURED!');
      toast.error('Call server not configured');
      throw new Error('No ICE servers configured');
    }
    
    const pc = new RTCPeerConnection(ICE_SERVERS);
    
    // Apply bandwidth limits for better mobile performance
    const applyBandwidthLimits = async (peerConnection) => {
      try {
        const senders = peerConnection.getSenders();
        for (const sender of senders) {
          if (sender.track?.kind === 'video') {
            const params = sender.getParameters();
            if (!params.encodings) {
              params.encodings = [{}];
            }
            // Limit video bitrate to 500kbps for mobile
            params.encodings[0].maxBitrate = 500000;
            params.encodings[0].scaleResolutionDownBy = 1.5;
            await sender.setParameters(params);
            console.log('[CallInterface] Applied video bitrate limit: 500kbps');
          } else if (sender.track?.kind === 'audio') {
            const params = sender.getParameters();
            if (!params.encodings) {
              params.encodings = [{}];
            }
            // Limit audio bitrate to 32kbps for voice
            params.encodings[0].maxBitrate = 32000;
            await sender.setParameters(params);
            console.log('[CallInterface] Applied audio bitrate limit: 32kbps');
          }
        }
      } catch (e) {
        console.warn('[CallInterface] Could not apply bitrate limits:', e.message);
      }
    };
    
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateType = event.candidate.type || 'unknown';
        const candidateProtocol = event.candidate.protocol || 'unknown';
        const candidateStr = event.candidate.candidate || '';
        
        // Check candidate type from the candidate string as well
        const isRelay = candidateStr.includes('typ relay');
        const isSrflx = candidateStr.includes('typ srflx');
        const isHost = candidateStr.includes('typ host');
        
        // Log candidate details for debugging
        console.log('[CallInterface] 📤 SENDING ICE Candidate:', {
          type: isRelay ? '✅ RELAY (TURN)' : (isSrflx ? 'SRFLX (STUN)' : (isHost ? 'HOST (local)' : candidateType)),
          protocol: candidateProtocol,
          address: event.candidate.address,
          port: event.candidate.port,
          candidateStr: candidateStr.slice(0, 80)
        });
        
        // Alert if we're getting relay candidates (TURN working)
        if (isRelay || candidateType === 'relay') {
          console.log('[CallInterface] ✅✅✅ RELAY CANDIDATE GENERATED - TURN IS WORKING!');
        }
        
        // Send to peer via signaling
        if (socketRef.current) {
          socketRef.current.emit('webrtc_ice_candidate', {
            peer_id: otherUser.id,
            candidate: event.candidate.toJSON(),
            call_id: callIdRef.current
          });
          console.log('[CallInterface] ✅ ICE candidate sent to peer via socket');
        } else {
          console.error('[CallInterface] ❌ Socket not available to send ICE candidate!');
        }
      } else {
        console.log('[CallInterface] 🏁 ICE gathering complete - no more candidates');
      }
    };
    
    pc.onicegatheringstatechange = () => {
      console.log('[CallInterface] ICE gathering state:', pc.iceGatheringState);
      if (pc.iceGatheringState === 'gathering') {
        setStatusMessage('Finding best connection path...');
      } else if (pc.iceGatheringState === 'complete') {
        console.log('[CallInterface] ICE gathering complete');
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      const connectionState = pc.connectionState;
      console.log('[CallInterface] 📊 ICE connection state:', state);
      console.log('[CallInterface] 📊 Connection state:', connectionState);
      console.log('[CallInterface] 📊 Signaling state:', pc.signalingState);
      
      // Log for monitoring
      console.log('[CallInterface] 📈 CALL METRICS:', {
        iceState: state,
        connectionState: connectionState,
        callDuration: callDuration,
        callId: callIdRef.current,
        timestamp: new Date().toISOString()
      });
      
      if (state === 'checking') {
        setStatusMessage('Connecting peer...');
      } else if (state === 'connected' || state === 'completed') {
        console.log('[CallInterface] ✅✅✅ CONNECTION SUCCESSFUL! ✅✅✅');
        setCallState('active');
        setIsReconnecting(false);
        
        // Clear connection timeout
        if (peerConnectionRef.current?._connectionTimeout) {
          clearTimeout(peerConnectionRef.current._connectionTimeout);
          delete peerConnectionRef.current._connectionTimeout;
        }
        
        if (!callStartTime) {
          setCallStartTime(Date.now());
        }
        setStatusMessage('Connected');
        toast.success('Call connected!');
        
        // Request Wake Lock to keep screen on during call
        requestWakeLock();
        
        // Apply bandwidth limits for better mobile performance
        applyBandwidthLimits(pc);
        
      } else if (state === 'failed') {
        console.error('[CallInterface] ❌ ICE connection FAILED');
        setStatusMessage('Connection failed');
        
        // Notify peer that connection failed
        socketRef.current?.emit('call_connection_failed', {
          peer_id: otherUser.id,
          call_id: callIdRef.current,
          reason: 'ICE connection failed'
        });
        
        toast.error('Connection failed. Please try again.');
        cleanup();
        setCallState('ended');
        setTimeout(() => onClose(), 2000);
        
      } else if (state === 'disconnected') {
        console.warn('[CallInterface] ⚠️ ICE DISCONNECTED - attempting ICE restart...');
        setStatusMessage('Reconnecting...');
        setIsReconnecting(true);
        
        // Try ICE restart first (wrap in async IIFE)
        (async () => {
          try {
            if (peerConnectionRef.current && peerConnectionRef.current.signalingState === 'stable') {
              console.log('[CallInterface] Attempting ICE restart...');
              const offer = await peerConnectionRef.current.createOffer({ iceRestart: true });
              await peerConnectionRef.current.setLocalDescription(offer);
              socketRef.current?.emit('webrtc_offer', {
                receiver_id: otherUser.id,
                offer: offer,
                call_id: callIdRef.current
              });
              console.log('[CallInterface] ICE restart offer sent');
            }
          } catch (e) {
            console.error('[CallInterface] ICE restart failed:', e);
          }
        })();
        
        // Give it 15 seconds to recover before giving up
        setTimeout(() => {
          if (peerConnectionRef.current?.iceConnectionState === 'disconnected' ||
              peerConnectionRef.current?.iceConnectionState === 'failed') {
            console.log('[CallInterface] ❌ Failed to reconnect after timeout');
            setStatusMessage('Connection lost');
            
            // Notify peer
            socketRef.current?.emit('call_connection_lost', {
              peer_id: otherUser.id,
              call_id: callIdRef.current
            });
            
            toast.error('Call disconnected - connection lost');
            cleanup();
            setCallState('ended');
            setTimeout(() => onClose(), 2000);
          }
        }, 15000);
        
      } else if (state === 'closed') {
        console.log('[CallInterface] ICE connection closed');
        if (callState === 'active') {
          // Unexpected close - notify peer
          socketRef.current?.emit('call_connection_lost', {
            peer_id: otherUser.id,
            call_id: callIdRef.current
          });
        }
      }
    };
    
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      console.log('[CallInterface] 📊 RTCPeerConnection state:', state);
      
      if (state === 'failed' || state === 'closed') {
        if (callState === 'active' || callState === 'connecting') {
          console.log('[CallInterface] ❌ Connection state failed/closed while in call');
          setStatusMessage('Connection lost');
          
          // Notify peer
          socketRef.current?.emit('call_connection_lost', {
            peer_id: otherUser.id,
            call_id: callIdRef.current
          });
          
          toast.error('Call disconnected');
          cleanup();
          setCallState('ended');
          setTimeout(() => onClose(), 2000);
        }
      }
    };
    
    pc.onsignalingstatechange = () => {
      console.log('[CallInterface] Signaling state:', pc.signalingState);
    };
    
    pc.ontrack = (event) => {
      console.log('========================================');
      console.log('[RECORDING] 🎯 ontrack EVENT FIRED');
      console.log('[RECORDING] Track kind:', event.track.kind);
      console.log('[RECORDING] Track readyState:', event.track.readyState);
      console.log('[RECORDING] Streams count:', event.streams.length);
      console.log('[RECORDING] localStreamRef.current:', localStreamRef.current ? 'SET' : 'NULL');
      console.log('========================================');
      
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
        console.log('[CallInterface] Set remote video srcObject');
        
        // Start merged recording when remote stream arrives
        onRemoteStreamReady(event.streams[0]);
        
        // Try to play the video
        remoteVideoRef.current.play().catch(e => {
          console.warn('[CallInterface] Remote video autoplay blocked:', e);
        });
      } else {
        console.error('[RECORDING] ❌ Cannot set remote stream - remoteVideoRef or stream missing');
      }
    };
    
    peerConnectionRef.current = pc;
    return pc;
  };
  
  const initiateCall = async (type) => {
    console.log('[CallInterface] Initiating', type, 'call to', otherUser.id);
    
    if (!isSocketConnected) {
      toast.error('Not connected to server');
      return;
    }
    
    try {
      setCallType(type);
      setCallState('calling');
      // Set speaker default: video = speaker ON, audio = earpiece (speaker OFF)
      setIsSpeakerOn(type === 'video');
      setStatusMessage('Initializing...');
      
      // IMPORTANT: Fetch fresh Twilio ICE servers before starting call
      console.log('[CallInterface] Fetching Twilio ICE servers...');
      await fetchIceServers();
      console.log('[CallInterface] ICE servers ready, proceeding with call');
      
      setStatusMessage('Starting call...');
      
      // Create call record on backend
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('conversation_id', conversation.id);
      formData.append('call_type', type);
      
      console.log('[CallInterface] Creating call record...');
      const response = await axios.post(`${API}/api/calls/initiate`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newCallId = response.data.call.id;
      console.log('[CallInterface] Call created with ID:', newCallId);
      setCallId(newCallId);
      callIdRef.current = newCallId;
      
      // Get media stream
      setStatusMessage('Accessing camera/microphone...');
      const stream = await getMediaStream(type === 'video');
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      // Create peer connection
      const pc = createPeerConnection();
      
      // Add tracks to peer connection
      stream.getTracks().forEach(track => {
        console.log('[CallInterface] Adding track to PC:', track.kind);
        pc.addTrack(track, stream);
      });
      
      // Create offer
      setStatusMessage('Creating offer...');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: type === 'video'
      });
      
      console.log('[CallInterface] Setting local description...');
      await pc.setLocalDescription(offer);
      
      // Notify other user
      console.log('[CallInterface] Sending call_user event...');
      socketRef.current.emit('call_user', {
        receiver_id: otherUser.id,
        call_data: {
          caller_id: currentUser.id,
          caller_name: currentUser.username || currentUser.phone || 'Unknown',
          call_type: type,
          call_id: newCallId
        }
      });
      
      // Send offer
      console.log('[CallInterface] Sending webrtc_offer...');
      socketRef.current.emit('webrtc_offer', {
        receiver_id: otherUser.id,
        offer: pc.localDescription.toJSON(),
        call_id: newCallId
      });
      
      setStatusMessage('Calling...');
      // Recording will start when remote stream arrives (onRemoteStreamReady)
      
      // Set a connection timeout - if not connected in 30 seconds, fail
      const connectionTimeout = setTimeout(() => {
        if (peerConnectionRef.current?.iceConnectionState !== 'connected' &&
            peerConnectionRef.current?.iceConnectionState !== 'completed') {
          console.error('[CallInterface] Connection timeout - no response in 30 seconds');
          toast.error('Call not answered or connection failed');
          cleanup();
          setCallState('ended');
          setTimeout(() => onClose(), 2000);
        }
      }, 30000);
      
      // Store timeout to clear it if call connects
      peerConnectionRef.current._connectionTimeout = connectionTimeout;
      
    } catch (error) {
      console.error('[CallInterface] Error initiating call:', error);
      toast.error('Failed to start call: ' + error.message);
      setCallState('idle');
      setStatusMessage('');
      cleanup();
    }
  };
  
  const handleIncomingOffer = async (data) => {
    console.log('[CallInterface] Handling incoming offer...');
    console.log('[CallInterface] Offer data:', JSON.stringify(data).slice(0, 200));
    
    try {
      setStatusMessage('Initializing...');
      
      // IMPORTANT: Fetch fresh Twilio ICE servers before creating peer connection
      console.log('[CallInterface] Fetching Twilio ICE servers for receiver...');
      await fetchIceServers();
      console.log('[CallInterface] ICE servers ready');
      
      setStatusMessage('Setting up connection...');
      
      // Get media
      console.log('[CallInterface] Getting media stream for callType:', callType);
      const stream = await getMediaStream(callType === 'video');
      localStreamRef.current = stream;
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        console.log('[CallInterface] Set local video source');
      }
      
      // Create peer connection
      const pc = createPeerConnection();
      
      // Add tracks BEFORE setting remote description
      stream.getTracks().forEach(track => {
        console.log('[CallInterface] Adding track to PC:', track.kind);
        pc.addTrack(track, stream);
      });
      
      // Set remote description (the offer)
      console.log('[CallInterface] Setting remote description (offer)...');
      const offer = new RTCSessionDescription(data.offer);
      await pc.setRemoteDescription(offer);
      console.log('[CallInterface] Remote description set successfully');
      
      // Process any pending ICE candidates
      console.log('[CallInterface] Processing', pendingCandidatesRef.current.length, 'pending candidates');
      for (const candidate of pendingCandidatesRef.current) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (e) {
          console.warn('[CallInterface] Failed to add pending candidate:', e);
        }
      }
      pendingCandidatesRef.current = [];
      
      // Create and send answer
      console.log('[CallInterface] Creating answer...');
      const answer = await pc.createAnswer();
      console.log('[CallInterface] Setting local description (answer)...');
      await pc.setLocalDescription(answer);
      
      // Get the caller_id from incomingCall or otherUser
      const callerId = incomingCall?.caller_id || otherUser.id;
      console.log('[CallInterface] Sending answer to caller:', callerId);
      
      socketRef.current.emit('webrtc_answer', {
        caller_id: callerId,
        answer: pc.localDescription.toJSON(),
        call_id: data.call_id || callIdRef.current
      });
      
      console.log('[CallInterface] Answer sent, waiting for ICE connection...');
      setCallState('connecting');
      setStatusMessage('Establishing connection...');
      // Recording will start when remote stream arrives (onRemoteStreamReady)
      
    } catch (error) {
      console.error('[CallInterface] Error handling offer:', error);
      toast.error('Failed to answer call: ' + error.message);
      setStatusMessage('Connection failed');
    }
  };
  
  const handleIncomingAnswer = async (data) => {
    console.log('[CallInterface] Handling incoming answer...');
    console.log('[CallInterface] Answer data:', JSON.stringify(data).slice(0, 200));
    
    try {
      if (!peerConnectionRef.current) {
        console.error('[CallInterface] No peer connection when receiving answer!');
        return;
      }
      
      console.log('[CallInterface] Current signaling state:', peerConnectionRef.current.signalingState);
      
      if (peerConnectionRef.current.signalingState !== 'have-local-offer') {
        console.warn('[CallInterface] Unexpected signaling state for answer:', peerConnectionRef.current.signalingState);
      }
      
      console.log('[CallInterface] Setting remote description (answer)...');
      const answer = new RTCSessionDescription(data.answer);
      await peerConnectionRef.current.setRemoteDescription(answer);
      console.log('[CallInterface] Remote description set successfully!');
      
      // Process pending ICE candidates
      console.log('[CallInterface] Processing', pendingCandidatesRef.current.length, 'pending candidates');
      for (const candidate of pendingCandidatesRef.current) {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
          console.log('[CallInterface] Added pending candidate successfully');
        } catch (e) {
          console.warn('[CallInterface] Failed to add pending candidate:', e);
        }
      }
      pendingCandidatesRef.current = [];
      
      setCallState('connecting');
      setStatusMessage('Establishing peer connection...');
      
    } catch (error) {
      console.error('[CallInterface] Error handling answer:', error);
      toast.error('Connection setup failed');
    }
  };
  
  const handleIncomingIceCandidate = async (data) => {
    try {
      const candidate = data.candidate;
      
      // Log candidate details for debugging
      if (candidate) {
        const candidateStr = candidate.candidate || '';
        const isRelay = candidateStr.includes('relay') || candidateStr.includes('typ relay');
        const isSrflx = candidateStr.includes('srflx');
        const isHost = candidateStr.includes('host');
        
        console.log('[CallInterface] 📥 Received ICE candidate:', {
          type: isRelay ? '✅ RELAY (TURN)' : (isSrflx ? 'SRFLX (STUN)' : (isHost ? 'HOST (local)' : 'unknown')),
          candidate: candidateStr.slice(0, 100),
          sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex
        });
        
        if (isRelay) {
          console.log('[CallInterface] ✅✅✅ RELAY CANDIDATE RECEIVED - TURN IS WORKING!');
        }
      }
      
      if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
        console.log('[CallInterface] Adding ICE candidate to peer connection...');
        await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        console.log('[CallInterface] ✅ ICE candidate added successfully');
      } else {
        console.log('[CallInterface] ⏳ Queuing ICE candidate (no remote description yet)');
        pendingCandidatesRef.current.push(candidate);
      }
    } catch (error) {
      console.error('[CallInterface] ❌ Error adding ICE candidate:', error);
    }
  };
  
  const acceptCall = async () => {
    console.log('[CallInterface] Accepting call...');
    hasAcceptedRef.current = true;
    
    try {
      // Notify backend
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/calls/${callId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Notify caller that we've accepted
      socketRef.current?.emit('call_accepted', {
        caller_id: otherUser.id,
        call_id: callId
      });
      
      setCallState('connecting');
      setStatusMessage('Connecting...');
      
      // If we already received the offer, process it now
      if (pendingOfferRef.current) {
        console.log('[CallInterface] Processing stored offer...');
        await handleIncomingOffer(pendingOfferRef.current);
        pendingOfferRef.current = null;
      } else {
        console.log('[CallInterface] Waiting for WebRTC offer...');
        setStatusMessage('Waiting for connection...');
      }
      
    } catch (error) {
      console.error('[CallInterface] Error accepting call:', error);
      toast.error('Failed to accept call');
    }
  };
  
  const rejectCall = () => {
    executeReject(async () => {
      console.log('[CallInterface] Rejecting call...');
      
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/api/calls/${callId}/reject`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        socketRef.current?.emit('call_rejected', {
          caller_id: otherUser.id,
          call_id: callId
        });
        
        // Log declined call to chat
        if (conversation?.id) {
          safeLogCallToChat(
            conversation.id,
            callType,
            'declined',
            0,
            otherUser?.id  // The caller
          );
        }
        
        cleanup();
        onClose();
        
      } catch (error) {
        console.error('[CallInterface] Error rejecting call:', error);
        onClose();
      }
    });
  };
  
  // ==================== NEW RECORDING SYSTEM ====================
  // Uses AudioContext to merge streams + chunked uploads
  
  const audioContextRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasIntervalRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const remoteStreamRef = useRef(null);
  const recordingStartedRef = useRef(false);
  
  const startMergedRecording = (localStream, remoteStream) => {
    try {
      console.log('========================================');
      console.log('[RECORDING] 🎬 STEP 1: Starting merged recording');
      console.log('[RECORDING] Call ID:', callIdRef.current);
      console.log('[RECORDING] Call Type:', callType);
      
      // CRITICAL: Don't start if already recording (prevents multiple instances)
      if (recordingStartedRef.current) {
        console.log('[RECORDING] ⚠️ Recording already started, SKIPPING to prevent duplicates');
        return;
      }
      
      // CRITICAL: Don't start if no call ID
      if (!callIdRef.current) {
        console.error('[RECORDING] ❌ No call ID - cannot start recording');
        return;
      }
      
      // STEP 2: Verify streams exist
      console.log('[RECORDING] 🎬 STEP 2: Verifying streams');
      console.log('[RECORDING] Local stream:', localStream ? 'EXISTS' : 'NULL');
      console.log('[RECORDING] Remote stream:', remoteStream ? 'EXISTS' : 'NULL');
      
      if (!localStream || !remoteStream) {
        console.error('[RECORDING] ❌ FAILED: Missing streams!');
        return;
      }
      
      // STEP 3: Verify audio tracks exist (CRITICAL for audio recording)
      const localAudioTracks = localStream.getAudioTracks();
      const remoteAudioTracks = remoteStream.getAudioTracks();
      
      console.log('[RECORDING] Local audio tracks:', localAudioTracks.length);
      console.log('[RECORDING] Remote audio tracks:', remoteAudioTracks.length);
      console.log('[RECORDING] Local video tracks:', localStream.getVideoTracks().length);
      console.log('[RECORDING] Remote video tracks:', remoteStream.getVideoTracks().length);
      
      // For audio calls, MUST have audio from both sides
      // For video calls, should have audio but video is primary
      if (localAudioTracks.length === 0) {
        console.error('[RECORDING] ❌ No local audio track - waiting...');
        // Retry after 500ms
        setTimeout(() => {
          if (!recordingStartedRef.current) {
            console.log('[RECORDING] 🔄 Retrying after local audio wait...');
            startMergedRecording(localStream, remoteStream);
          }
        }, 500);
        return;
      }
      
      if (remoteAudioTracks.length === 0) {
        console.error('[RECORDING] ❌ No remote audio track - waiting...');
        // Retry after 500ms
        setTimeout(() => {
          if (!recordingStartedRef.current) {
            console.log('[RECORDING] 🔄 Retrying after remote audio wait...');
            startMergedRecording(localStream, remoteStream);
          }
        }, 500);
        return;
      }
      
      // Store stream refs
      remoteStreamRef.current = remoteStream;
      localStreamRef.current = localStream;
      
      // SET FLAG IMMEDIATELY to prevent any race conditions
      recordingStartedRef.current = true;
      console.log('[RECORDING] ✅ Recording flag set to TRUE - no more starts allowed');
      
      const isVideoCall = callType === 'video';
      
      if (isVideoCall) {
        console.log('[RECORDING] 📹 Starting VIDEO recording...');
        startVideoRecording(localStream, remoteStream);
      } else {
        console.log('[RECORDING] 🎤 Starting AUDIO recording...');
        startAudioRecording(localStream, remoteStream);
      }
      
      console.log('========================================');
      
    } catch (error) {
      console.error('[RECORDING] ❌ FATAL ERROR starting recording:', error);
      recordingStartedRef.current = false; // Reset on error so it can be retried
    }
  };
  
  const startAudioRecording = (localStream, remoteStream) => {
    try {
      console.log('[RECORDING] 🎤 STEP 3: Creating AudioContext for AUDIO call');
      console.log('[RECORDING] 🎤 Local audio tracks:', localStream.getAudioTracks().length);
      console.log('[RECORDING] 🎤 Remote audio tracks:', remoteStream.getAudioTracks().length);
      
      // Create AudioContext to merge streams
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      console.log('[RECORDING] 🎤 AudioContext state:', audioContext.state);
      
      // Resume AudioContext if suspended (required on some browsers/iOS)
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          console.log('[RECORDING] 🎤 AudioContext resumed from suspended');
        });
      }
      
      // Create destination for merged audio
      const destination = audioContext.createMediaStreamDestination();
      console.log('[RECORDING] 🎤 Destination created');
      
      // Connect local audio
      const localAudioTracks = localStream.getAudioTracks();
      console.log('[RECORDING] 🎤 Local audio tracks to connect:', localAudioTracks.length);
      if (localAudioTracks.length > 0) {
        const localSource = audioContext.createMediaStreamSource(
          new MediaStream([localAudioTracks[0]])
        );
        localSource.connect(destination);
        console.log('[RECORDING] 🎤 ✅ Local audio connected');
      } else {
        console.error('[RECORDING] 🎤 ❌ No local audio tracks!');
      }
      
      // Connect remote audio
      const remoteAudioTracks = remoteStream.getAudioTracks();
      console.log('[RECORDING] 🎤 Remote audio tracks to connect:', remoteAudioTracks.length);
      if (remoteAudioTracks.length > 0) {
        const remoteSource = audioContext.createMediaStreamSource(
          new MediaStream([remoteAudioTracks[0]])
        );
        remoteSource.connect(destination);
        console.log('[RECORDING] 🎤 ✅ Remote audio connected');
      } else {
        console.error('[RECORDING] 🎤 ❌ No remote audio tracks!');
      }
      
      // Create MediaRecorder with merged stream
      const mergedStream = destination.stream;
      console.log('[RECORDING] 🎤 STEP 4: Creating MediaRecorder');
      console.log('[RECORDING] 🎤 Merged stream tracks:', mergedStream.getTracks().length);
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      console.log('[RECORDING] 🎤 Using mimeType:', mimeType);
      
      const recorder = new MediaRecorder(mergedStream, { 
        mimeType,
        audioBitsPerSecond: 64000
      });
      
      mediaRecorderRef.current = recorder;
      chunkIndexRef.current = 0;
      
      recorder.ondataavailable = async (e) => {
        console.log('[RECORDING] 🎤 ondataavailable fired, size:', e.data.size);
        if (e.data.size > 0) {
          const chunkIndex = chunkIndexRef.current++;
          console.log(`[RECORDING] 🎤 CHUNK ${chunkIndex}: ${e.data.size} bytes - uploading...`);
          
          // Upload chunk asynchronously (don't await)
          uploadChunk(e.data, chunkIndex, false);
        } else {
          console.warn('[RECORDING] 🎤 ⚠️ Empty chunk received!');
        }
      };
      
      recorder.onerror = (e) => {
        console.error('[RECORDING] 🎤 ❌ MediaRecorder ERROR:', e.error);
      };
      
      recorder.onstop = () => {
        console.log('[RECORDING] 🎤 Audio recording stopped, total chunks:', chunkIndexRef.current);
      };
      
      // Start continuous recording with 5-second chunks
      recorder.start(5000);
      console.log('[RECORDING] 🎤 ✅ MediaRecorder.start(5000) called');
      console.log('[RECORDING] 🎤 MediaRecorder state:', recorder.state);
      
      combinedStreamRef.current = mergedStream;
      
    } catch (error) {
      console.error('[RECORDING] 🎤 ❌ Error starting audio recording:', error);
    }
  };
  
  const startVideoRecording = (localStream, remoteStream) => {
    try {
      console.log('[RECORDING] 📹 Starting VIDEO recording setup...');
      console.log('[RECORDING] 📹 Local stream audio tracks:', localStream.getAudioTracks().length);
      console.log('[RECORDING] 📹 Remote stream audio tracks:', remoteStream.getAudioTracks().length);
      console.log('[RECORDING] 📹 Local stream video tracks:', localStream.getVideoTracks().length);
      console.log('[RECORDING] 📹 Remote stream video tracks:', remoteStream.getVideoTracks().length);
      
      // IMPORTANT: Check if we have video tracks
      if (localStream.getVideoTracks().length === 0 || remoteStream.getVideoTracks().length === 0) {
        console.error('[RECORDING] 📹 ❌ Missing video tracks! Falling back to audio-only recording');
        // Fall back to audio recording if no video tracks
        startAudioRecording(localStream, remoteStream);
        return;
      }
      
      // Create hidden canvas for video composition
      // Canvas size = 640x240 (two 320x240 videos side by side)
      const canvas = document.createElement('canvas');
      canvas.width = 640;   // 320 + 320 side by side
      canvas.height = 240;  // 240p height
      canvasRef.current = canvas;
      console.log('[RECORDING] 📹 Canvas created:', canvas.width, 'x', canvas.height);
      
      const ctx = canvas.getContext('2d');
      
      // Create video elements for drawing
      const localVideo = document.createElement('video');
      localVideo.srcObject = localStream;
      localVideo.muted = true;
      localVideo.playsInline = true;
      localVideo.autoplay = true;
      localVideo.setAttribute('playsinline', 'true');  // iOS specific
      localVideo.setAttribute('webkit-playsinline', 'true');  // Older iOS
      
      const remoteVideo = document.createElement('video');
      remoteVideo.srcObject = remoteStream;
      remoteVideo.muted = true;  // Muted for recording (we capture audio separately)
      remoteVideo.playsInline = true;
      remoteVideo.autoplay = true;
      remoteVideo.setAttribute('playsinline', 'true');  // iOS specific
      remoteVideo.setAttribute('webkit-playsinline', 'true');  // Older iOS
      
      // Wait for videos to be ready before starting recording
      let localReady = false;
      let remoteReady = false;
      
      const checkAndStartRecording = () => {
        if (localReady && remoteReady) {
          console.log('[RECORDING] 📹 ✅ Both videos ready, starting canvas capture');
          console.log('[RECORDING] 📹 Local video size:', localVideo.videoWidth, 'x', localVideo.videoHeight);
          console.log('[RECORDING] 📹 Remote video size:', remoteVideo.videoWidth, 'x', remoteVideo.videoHeight);
          startCanvasRecording(canvas, ctx, localVideo, remoteVideo, localStream, remoteStream);
        }
      };
      
      localVideo.onloadedmetadata = () => {
        console.log('[RECORDING] 📹 Local video metadata loaded:', localVideo.videoWidth, 'x', localVideo.videoHeight);
        localVideo.play().then(() => {
          localReady = true;
          console.log('[RECORDING] 📹 Local video playing');
          checkAndStartRecording();
        }).catch(e => {
          console.error('[RECORDING] Local video play error:', e);
          // Still mark as ready - iOS might auto-play anyway
          localReady = true;
          checkAndStartRecording();
        });
      };
      
      remoteVideo.onloadedmetadata = () => {
        console.log('[RECORDING] 📹 Remote video metadata loaded:', remoteVideo.videoWidth, 'x', remoteVideo.videoHeight);
        remoteVideo.play().then(() => {
          remoteReady = true;
          console.log('[RECORDING] 📹 Remote video playing');
          checkAndStartRecording();
        }).catch(e => {
          console.error('[RECORDING] Remote video play error:', e);
          // Still mark as ready
          remoteReady = true;
          checkAndStartRecording();
        });
      };
      
      // Handle video errors
      localVideo.onerror = (e) => {
        console.error('[RECORDING] 📹 Local video error:', e);
      };
      
      remoteVideo.onerror = (e) => {
        console.error('[RECORDING] 📹 Remote video error:', e);
      };
      
      // Fallback: Start after 3 seconds even if not fully ready (increased from 2s)
      setTimeout(() => {
        if (!localReady || !remoteReady) {
          console.log('[RECORDING] 📹 ⚠️ Timeout (3s) - starting anyway (local:', localReady, ', remote:', remoteReady, ')');
          console.log('[RECORDING] 📹 Local video state:', localVideo.readyState, 'size:', localVideo.videoWidth, 'x', localVideo.videoHeight);
          console.log('[RECORDING] 📹 Remote video state:', remoteVideo.readyState, 'size:', remoteVideo.videoWidth, 'x', remoteVideo.videoHeight);
          localReady = true;
          remoteReady = true;
          startCanvasRecording(canvas, ctx, localVideo, remoteVideo, localStream, remoteStream);
        }
      }, 3000);
      
    } catch (error) {
      console.error('[RECORDING] 📹 ❌ Error starting video recording:', error);
      // Fall back to audio recording
      console.log('[RECORDING] 📹 Falling back to audio-only recording');
      startAudioRecording(localStream, remoteStream);
    }
  };
  
  const startCanvasRecording = (canvas, ctx, localVideo, remoteVideo, localStream, remoteStream) => {
    try {
      console.log('[RECORDING] 📹 Starting canvas recording...');
      
      // Continuous drawing using requestAnimationFrame (CRITICAL for quality)
      let animationFrameId = null;
      const draw = () => {
        // Clear canvas with black background
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw local video on left (320x240)
        try {
          if (localVideo.readyState >= 2 && localVideo.videoWidth > 0) {
            ctx.drawImage(localVideo, 0, 0, 320, 240);
          }
        } catch (e) {
          // Ignore draw errors
        }
        
        // Draw remote video on right (320x240)
        try {
          if (remoteVideo.readyState >= 2 && remoteVideo.videoWidth > 0) {
            ctx.drawImage(remoteVideo, 320, 0, 320, 240);
          }
        } catch (e) {
          // Ignore draw errors
        }
        
        // Continue drawing loop
        animationFrameId = requestAnimationFrame(draw);
      };
      
      // Start the continuous drawing loop
      draw();
      console.log('[RECORDING] 📹 Canvas draw loop started with requestAnimationFrame');
      
      // Store animationFrameId for cleanup
      canvasIntervalRef.current = animationFrameId;
      
      // Create AudioContext to merge audio streams
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      console.log('[RECORDING] 📹 AudioContext created, state:', audioContext.state);
      
      const audioDestination = audioContext.createMediaStreamDestination();
      
      // Connect local audio
      const localAudioTracks = localStream.getAudioTracks();
      console.log('[RECORDING] 📹 Local audio tracks:', localAudioTracks.length);
      if (localAudioTracks.length > 0) {
        const localSource = audioContext.createMediaStreamSource(
          new MediaStream([localAudioTracks[0]])
        );
        localSource.connect(audioDestination);
        console.log('[RECORDING] 📹 Local audio connected to destination');
      }
      
      // Connect remote audio
      const remoteAudioTracks = remoteStream.getAudioTracks();
      console.log('[RECORDING] 📹 Remote audio tracks:', remoteAudioTracks.length);
      if (remoteAudioTracks.length > 0) {
        const remoteSource = audioContext.createMediaStreamSource(
          new MediaStream([remoteAudioTracks[0]])
        );
        remoteSource.connect(audioDestination);
        console.log('[RECORDING] 📹 Remote audio connected to destination');
      }
      
      // Capture canvas as video stream at 15 FPS
      const canvasStream = canvas.captureStream(15);
      console.log('[RECORDING] 📹 Canvas stream captured, video tracks:', canvasStream.getVideoTracks().length);
      
      // Combine canvas video + merged audio
      const combinedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...audioDestination.stream.getAudioTracks()
      ]);
      console.log('[RECORDING] 📹 Combined stream created, total tracks:', combinedStream.getTracks().length);
      
      // Create MediaRecorder with iOS-compatible settings
      // iOS Safari supports MP4/H.264, Android/Chrome supports WebM/VP8
      let mimeType = 'video/webm;codecs=vp8,opus';
      
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Try WebM without codecs
        mimeType = 'video/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          // Try MP4 for iOS
          mimeType = 'video/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            // Last resort - let browser decide
            mimeType = '';
          }
        }
      }
      console.log('[RECORDING] 📹 Using mimeType:', mimeType || 'default');
      
      const recorderOptions = {
        videoBitsPerSecond: 500000, // 500kbps for 240p
        audioBitsPerSecond: 64000
      };
      
      if (mimeType) {
        recorderOptions.mimeType = mimeType;
      }
      
      const recorder = new MediaRecorder(combinedStream, recorderOptions);
      
      mediaRecorderRef.current = recorder;
      chunkIndexRef.current = 0;
      
      recorder.ondataavailable = async (e) => {
        console.log('[RECORDING] 📹 ondataavailable - size:', e.data.size);
        if (e.data.size > 0) {
          const chunkIndex = chunkIndexRef.current++;
          console.log(`[RECORDING] 📹 Video chunk ${chunkIndex}: ${e.data.size} bytes - uploading...`);
          
          // Upload chunk asynchronously (don't await - keep recording)
          uploadChunk(e.data, chunkIndex, false);
        } else {
          console.warn('[RECORDING] 📹 ⚠️ Empty video chunk received');
        }
      };
      
      recorder.onerror = (e) => {
        console.error('[RECORDING] 📹 ❌ MediaRecorder error:', e.error);
      };
      
      recorder.onstop = () => {
        console.log('[RECORDING] 📹 Video recording stopped');
        // Cancel animation frame
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
      
      // Start continuous recording with 5-second chunks
      recorder.start(5000);
      console.log('[RECORDING] 📹 ✅ MediaRecorder started (5s chunks, 640x240, 15fps)');
      console.log('[RECORDING] 📹 MediaRecorder state:', recorder.state);
      
      combinedStreamRef.current = combinedStream;
      
    } catch (error) {
      console.error('[RECORDING] 📹 ❌ Error starting video recording:', error);
    }
  };
  
  const uploadChunk = async (blob, chunkIndex, isFinal) => {
    console.log(`[RECORDING] 📤 UPLOAD STEP: Chunk ${chunkIndex}, size: ${blob.size}, call_id: ${callIdRef.current}`);
    
    // CRITICAL: Skip empty or tiny chunks
    if (blob.size < 100) {
      console.warn(`[RECORDING] ⚠️ Skipping chunk ${chunkIndex} - too small (${blob.size} bytes)`);
      return;
    }
    
    if (!callIdRef.current) {
      console.error('[RECORDING] ❌ UPLOAD FAILED: No call_id!');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[RECORDING] ❌ UPLOAD FAILED: No auth token!');
        return;
      }
      
      const formData = new FormData();
      formData.append('chunk', blob, `chunk_${chunkIndex}.webm`);
      formData.append('chunk_index', chunkIndex.toString());
      formData.append('timestamp', Date.now().toString());
      formData.append('is_final', isFinal.toString());
      
      console.log(`[RECORDING] 📤 Uploading to: ${API}/api/calls/${callIdRef.current}/chunk`);
      
      const response = await axios.post(
        `${API}/api/calls/${callIdRef.current}/chunk`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 15000  // Increased timeout
        }
      );
      
      console.log(`[RECORDING] 📤 Upload response:`, response.data);
      
      if (response.data.status === 'success') {
        console.log(`[RECORDING] ✅ Chunk ${chunkIndex} uploaded successfully`);
      } else if (response.data.retry) {
        console.log(`[RECORDING] ⚠️ Retrying chunk ${chunkIndex}...`);
        setTimeout(() => uploadChunk(blob, chunkIndex, isFinal), 2000);
      } else {
        console.error(`[RECORDING] ❌ Chunk ${chunkIndex} upload failed:`, response.data);
      }
      
    } catch (error) {
      console.error(`[RECORDING] ❌ UPLOAD ERROR for chunk ${chunkIndex}:`, error.response?.data || error.message);
      // Retry on failure
      console.log(`[RECORDING] ⚠️ Will retry chunk ${chunkIndex} in 3 seconds...`);
      setTimeout(() => uploadChunk(blob, chunkIndex, isFinal), 3000);
    }
  };
  
  const stopRecording = async () => {
    try {
      console.log('[RECORDING] 🛑 Stopping recording...');
      
      // Stop canvas drawing (requestAnimationFrame)
      if (canvasIntervalRef.current) {
        cancelAnimationFrame(canvasIntervalRef.current);
        canvasIntervalRef.current = null;
        console.log('[RECORDING] 🛑 Animation frame cancelled');
      }
      
      // Stop MediaRecorder and wait for final chunk
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('[RECORDING] 🛑 Stopping MediaRecorder, state:', mediaRecorderRef.current.state);
        mediaRecorderRef.current.stop();
      }
      
      // Close AudioContext
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log('[RECORDING] 🛑 Closing AudioContext');
        audioContextRef.current.close();
      }
      
      console.log('[RECORDING] 🛑 Recording stopped, total chunks uploaded:', chunkIndexRef.current);
      recordingStartedRef.current = false;
      
    } catch (error) {
      console.error('[RECORDING] ❌ Error stopping recording:', error);
    }
  };
  
  // Called when remote stream arrives
  const onRemoteStreamReady = (remoteStream) => {
    console.log('========================================');
    console.log('[RECORDING] 🎯 onRemoteStreamReady CALLED');
    console.log('[RECORDING] Remote stream:', remoteStream ? 'EXISTS' : 'NULL');
    console.log('[RECORDING] Local stream ref:', localStreamRef.current ? 'EXISTS' : 'NULL');
    
    // IMPORTANT: Only the CALLER records to avoid duplicate chunks from both devices
    // The receiver does NOT record - this ensures clean, single-source recordings
    const isCaller = !incomingCall; // If there's no incomingCall, we are the caller
    console.log('[RECORDING] Is Caller:', isCaller);
    
    if (!isCaller) {
      console.log('[RECORDING] 📵 Receiver side - NOT recording (only caller records)');
      return;
    }
    
    if (localStreamRef.current && remoteStream) {
      console.log('[RECORDING] ✅ Caller with both streams ready - starting recording');
      startMergedRecording(localStreamRef.current, remoteStream);
    } else {
      console.error('[RECORDING] ❌ Cannot start recording - missing streams');
      console.error('[RECORDING] Local:', !!localStreamRef.current, 'Remote:', !!remoteStream);
    }
  };
  
  // ==================== END NEW RECORDING SYSTEM ====================
  
  const isEndingCallRef = useRef(false);  // Prevent multiple end call clicks
  
  const endCall = () => {
    // Prevent multiple clicks
    if (isEndingCallRef.current) {
      return;
    }
    isEndingCallRef.current = true;
    
    // IMMEDIATE: Set state and close UI
    setCallState('ended');
    
    // IMMEDIATE: Notify peer
    socketRef.current?.emit('end_call', {
      peer_id: otherUser.id,
      call_id: callIdRef.current
    });
    
    // IMMEDIATE: Stop recording
    stopRecording();
    
    // IMMEDIATE: Cleanup media streams
    cleanup();
    
    // IMMEDIATE: Close the call UI
    toast.success('Call ended');
    onClose();
    
    // BACKGROUND: Send duration to backend (non-blocking)
    const duration = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : 0;
    const callId = callIdRef.current;
    
    if (callId) {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('duration', duration.toString());
      
      // Fire and forget - don't wait for response
      axios.post(`${API}/api/calls/${callId}/end`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }).catch(() => {}); // Silent fail
    }
    
    // Log completed call to chat (only if call was actually connected)
    if (callStartTime && conversation?.id) {
      safeLogCallToChat(
        conversation.id,
        callType,
        'completed',
        duration,
        currentUser?.id
      );
    }
  };
  
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };
  
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };
  
  // PiP drag handlers for touch and mouse
  const handlePipDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    const pip = pipRef.current;
    if (pip) {
      const rect = pip.getBoundingClientRect();
      dragStartRef.current = {
        x: clientX,
        y: clientY,
        pipX: rect.left,
        pipY: rect.top
      };
    }
  };
  
  const handlePipDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;
    
    const newX = dragStartRef.current.pipX + deltaX;
    const newY = dragStartRef.current.pipY + deltaY;
    
    // Get container bounds
    const container = pipRef.current?.parentElement;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const pipRect = pipRef.current.getBoundingClientRect();
      
      // Constrain to container bounds
      const maxX = containerRect.width - pipRect.width - 16;
      const maxY = containerRect.height - pipRect.height - 100; // Account for controls
      const minX = 16;
      const minY = 60; // Account for header
      
      setPipPosition({
        x: Math.max(minX, Math.min(maxX, newX - containerRect.left)),
        y: Math.max(minY, Math.min(maxY, newY - containerRect.top))
      });
    }
  };
  
  const handlePipDragEnd = () => {
    setIsDragging(false);
  };
  
  // Add/remove global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handlePipDragMove(e);
      const handleEnd = () => handlePipDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove, { passive: false });
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-lg p-0 overflow-hidden border-0"
        hideCloseButton={true}
        style={{ 
          background: 'linear-gradient(135deg, #0a0f2c 0%, #1a1f3c 100%)',
          height: '85vh',
          maxHeight: '700px'
        }}
      >
        {/* Custom Close button - positioned properly for call UI */}
        <button
          onClick={() => {
            if (callState === 'idle' || callState === 'ended') {
              onClose();
            } else {
              endCall();
            }
          }}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-white/10 transition"
        >
          <X className="h-5 w-5 text-white/70" />
        </button>
        
        {/* Connection status indicator */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isSocketConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-xs text-white/50">
            {isSocketConnected ? 'Connected' : 'Connecting...'}
          </span>
        </div>
        
        <div className="relative h-full flex flex-col">
          {/* Video area */}
          <div className="flex-1 relative bg-slate-900 flex items-center justify-center overflow-hidden">
            {/* Remote video */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
              style={{ display: callState === 'active' && callType === 'video' ? 'block' : 'none' }}
            />
            
            {/* Call duration overlay for video calls */}
            {callState === 'active' && callType === 'video' && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-white font-medium text-sm">
                    {formatDuration(callDuration)}
                  </span>
                </div>
              </div>
            )}
            
            {/* Avatar/Status */}
            {(callState !== 'active' || callType === 'audio') && (
              <div className="text-center p-8">
                <div 
                  className={`w-28 h-28 rounded-full mx-auto mb-6 flex items-center justify-center ${
                    callState === 'calling' || callState === 'ringing' ? 'animate-pulse' : ''
                  }`}
                  style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                >
                  {callState === 'ringing' ? (
                    <PhoneIncoming className="h-14 w-14 text-white" />
                  ) : (
                    <span className="text-4xl font-bold text-white">
                      {(otherUser?.name || otherUser?.username || '?')[0].toUpperCase()}
                    </span>
                  )}
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">
                  {otherUser?.name || otherUser?.username || 'Unknown'}
                </h2>
                
                <p className="text-gray-400 mb-2">
                  {callState === 'idle' && 'Choose call type'}
                  {callState === 'calling' && 'Calling...'}
                  {callState === 'ringing' && `Incoming ${callType} call`}
                  {callState === 'connecting' && 'Connecting...'}
                  {callState === 'active' && !isReconnecting && formatDuration(callDuration)}
                  {callState === 'active' && isReconnecting && '⚠️ Reconnecting...'}
                  {callState === 'ended' && 'Call ended'}
                </p>
                
                {/* Reconnecting indicator */}
                {isReconnecting && (
                  <div className="flex items-center gap-2 text-yellow-500 text-sm animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 animate-ping"></div>
                    <span>Connection interrupted, trying to reconnect...</span>
                  </div>
                )}
                
                {statusMessage && !isReconnecting && (
                  <p className="text-xs text-gray-500">{statusMessage}</p>
                )}
              </div>
            )}
            
            {/* Local video PiP - Draggable */}
            {callType === 'video' && (callState === 'calling' || callState === 'connecting' || callState === 'active') && (
              <div 
                ref={pipRef}
                className={`absolute w-28 h-36 rounded-xl overflow-hidden border-2 shadow-xl bg-slate-800 cursor-move select-none ${
                  isDragging ? 'border-cyan-400 scale-105' : 'border-white/30'
                }`}
                style={{
                  // Use custom position if set, otherwise default to bottom-right
                  ...(pipPosition.x !== null ? {
                    left: pipPosition.x,
                    top: pipPosition.y,
                  } : {
                    bottom: '6rem',
                    right: '1rem',
                  }),
                  transition: isDragging ? 'none' : 'transform 0.2s, border-color 0.2s',
                  zIndex: 20,
                  touchAction: 'none', // Prevent scroll while dragging on touch
                }}
                onMouseDown={handlePipDragStart}
                onTouchStart={handlePipDragStart}
              >
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover pointer-events-none"
                />
                {isVideoOff && (
                  <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
                    <VideoOff className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                {/* Drag indicator */}
                <div className="absolute top-1 left-1/2 transform -translate-x-1/2">
                  <div className="w-8 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            )}
          </div>
          
          {/* Controls */}
          <div className="p-6 flex items-center justify-center gap-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
            {/* Idle - show call buttons */}
            {callState === 'idle' && (
              <>
                <Button
                  onClick={() => initiateCall('audio')}
                  disabled={!isSocketConnected}
                  className="rounded-full w-16 h-16 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                >
                  <Phone className="h-7 w-7" />
                </Button>
                <Button
                  onClick={() => initiateCall('video')}
                  disabled={!isSocketConnected}
                  className="rounded-full w-16 h-16 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  <Video className="h-7 w-7" />
                </Button>
              </>
            )}
            
            {/* Ringing - accept/reject */}
            {callState === 'ringing' && (
              <div className="flex items-center justify-center gap-8">
                <div className="flex flex-col items-center gap-2">
                  <Button
                    onClick={rejectCall}
                    className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700"
                  >
                    <PhoneOff className="h-7 w-7" />
                  </Button>
                  <span className="text-xs text-white/70">Decline</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <Button
                    onClick={acceptCall}
                    className="rounded-full w-16 h-16 bg-green-600 hover:bg-green-700 animate-pulse"
                  >
                    <Phone className="h-7 w-7" />
                  </Button>
                  <span className="text-xs text-white/70">Accept</span>
                </div>
              </div>
            )}
            
            {/* In-call controls */}
            {(callState === 'calling' || callState === 'connecting' || callState === 'active') && (
              <div className="flex items-center justify-center gap-6 flex-wrap">
                {/* Mute button */}
                <div className="flex flex-col items-center gap-1">
                  <Button
                    onClick={toggleMute}
                    className={`rounded-full w-14 h-14 ${isMuted ? 'bg-red-600' : 'bg-gray-700'} hover:opacity-80`}
                  >
                    {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                  </Button>
                  <span className="text-xs text-white/70">{isMuted ? 'Unmute' : 'Mute'}</span>
                </div>
                
                {/* Speaker toggle button - always show */}
                <div className="flex flex-col items-center gap-1">
                  <Button
                    onClick={toggleSpeaker}
                    className={`rounded-full w-14 h-14 ${isSpeakerOn ? 'bg-blue-600' : 'bg-gray-700'} hover:opacity-80`}
                  >
                    {isSpeakerOn ? (
                      <Volume2 className="h-6 w-6" />
                    ) : (
                      <Phone className="h-6 w-6" />
                    )}
                  </Button>
                  <span className="text-xs text-white/70">{isSpeakerOn ? 'Speaker' : 'Earpiece'}</span>
                </div>
                
                {/* Video toggle - only for video calls */}
                {callType === 'video' && (
                  <div className="flex flex-col items-center gap-1">
                    <Button
                      onClick={toggleVideo}
                      className={`rounded-full w-14 h-14 ${isVideoOff ? 'bg-red-600' : 'bg-gray-700'} hover:opacity-80`}
                    >
                      {isVideoOff ? <VideoOff className="h-6 w-6" /> : <Video className="h-6 w-6" />}
                    </Button>
                    <span className="text-xs text-white/70">{isVideoOff ? 'Video Off' : 'Video'}</span>
                  </div>
                )}
                
                {/* End call button */}
                <div className="flex flex-col items-center gap-1">
                  <Button
                    onClick={endCall}
                    className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700"
                  >
                    <PhoneOff className="h-7 w-7" />
                  </Button>
                  <span className="text-xs text-white/70">End</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
